import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/base/SEOHead';
import RelatedResources from '../../../components/feature/RelatedResources';

export default function FinancialProjectionsGuidePage() {
  const projectionTypes = [
    {
      title: 'Revenue Forecasting',
      icon: 'ri-line-chart-line',
      description: 'Estimate your future sales and income streams',
      steps: [
        'Research El Paso market size and growth trends',
        'Identify your target customer segments and their spending capacity',
        'Calculate realistic market share based on competition',
        'Project monthly sales for the first year, quarterly for years 2-3',
        'Account for seasonal variations in El Paso\'s economy',
        'Include multiple revenue streams if applicable'
      ]
    },
    {
      title: 'Expense Budgeting',
      icon: 'ri-wallet-line',
      description: 'Plan for all business costs and operating expenses',
      steps: [
        'List fixed costs: rent, utilities, insurance, salaries',
        'Calculate variable costs: materials, shipping, commissions',
        'Research El Paso-specific costs: permits, licenses, taxes',
        'Include one-time startup expenses separately',
        'Plan for marketing and advertising budget',
        'Add a contingency buffer of 10-15% for unexpected costs'
      ]
    },
    {
      title: 'Cash Flow Projections',
      icon: 'ri-exchange-dollar-line',
      description: 'Track when money comes in and goes out',
      steps: [
        'Create monthly cash flow statements for year one',
        'Account for payment terms and collection delays',
        'Plan for seasonal cash flow fluctuations',
        'Identify potential cash shortfalls in advance',
        'Include loan payments and interest expenses',
        'Maintain a minimum cash reserve for emergencies'
      ]
    },
    {
      title: 'Break-Even Analysis',
      icon: 'ri-scales-line',
      description: 'Determine when your business becomes profitable',
      steps: [
        'Calculate your fixed costs per month',
        'Determine your average sale price per unit',
        'Calculate variable cost per unit',
        'Compute break-even point: Fixed Costs ÷ (Price - Variable Cost)',
        'Estimate timeline to reach break-even in El Paso market',
        'Plan strategies to accelerate reaching break-even'
      ]
    }
  ];

  const elPasoConsiderations = [
    {
      title: 'Local Economic Factors',
      icon: 'ri-map-pin-line',
      points: [
        'Border economy dynamics and cross-border trade',
        'Military presence impact (Fort Bliss)',
        'Tourism and hospitality seasonal patterns',
        'Manufacturing and logistics sector opportunities'
      ]
    },
    {
      title: 'Cost of Doing Business',
      icon: 'ri-price-tag-line',
      points: [
        'Lower commercial rent compared to major Texas cities',
        'Competitive labor costs and wage expectations',
        'Texas franchise tax and local tax considerations',
        'Utility costs and business insurance rates in El Paso'
      ]
    },
    {
      title: 'Market Opportunities',
      icon: 'ri-rocket-line',
      points: [
        'Growing population and expanding consumer base',
        'Bilingual market advantages',
        'Strategic location for distribution and logistics',
        'Emerging tech and innovation sectors'
      ]
    }
  ];

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://elpasotexasbusiness.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Resources",
        "item": "https://elpasotexasbusiness.com/resources"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Financial Projections Guide",
        "item": "https://elpasotexasbusiness.com/resources/financial-projections-guide"
      }
    ]
  };

  const relatedResources = [
    {
      title: 'Business Plan Template',
      description: 'Create a comprehensive business plan with our step-by-step template covering all essential sections.',
      path: '/resources/business-plan-template',
      icon: 'ri-file-text-line'
    },
    {
      title: 'Small Business Loans',
      description: 'Explore SBA loans, microloans, and local lending options to fund your El Paso business.',
      path: '/resources/small-business-loans',
      icon: 'ri-money-dollar-circle-line'
    },
    {
      title: 'Market Research Tools',
      description: 'Access demographic data and market intelligence tools to validate your financial assumptions.',
      path: '/resources/market-research-tools',
      icon: 'ri-search-line'
    }
  ];

  return (
    <>
      <SEOHead
        title="Financial Projections Guide for El Paso Startups"
        description="Learn to create accurate financial projections for your El Paso business. Includes revenue forecasting, expense budgeting, cash flow analysis, and break-even calculations with Texas-specific examples."
        canonical="https://elpasotexasbusiness.com/resources/financial-projections-guide"
        ogImage="https://public.readdy.ai/ai/img_res/45d3d7a058ca516620544e68ab7e4a63.jpg"
      />
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        
        <main id="main-content" className="flex-grow">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200 pt-24">
            <div className="max-w-7xl mx-auto px-6 py-6">
              <Link 
                to="/resources" 
                className="inline-flex items-center space-x-2 text-[#D2691E] hover:text-[#B8571A] transition-colors cursor-pointer"
                aria-label="Return to Resources page"
              >
                <i className="ri-arrow-left-line"></i>
                <span className="font-semibold text-sm">Back to Resources</span>
              </Link>
            </div>
          </nav>

          <div className="max-w-7xl mx-auto px-6 py-16">
            <div className="mb-16">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-20 h-20 bg-[#D2691E] rounded-2xl flex items-center justify-center">
                  <i className="ri-line-chart-line text-4xl text-white"></i>
                </div>
                <div>
                  <h1 className="text-5xl font-bold text-gray-900">Financial Projections Guide</h1>
                  <p className="text-lg text-gray-600 mt-2">Create accurate financial forecasts for your El Paso business</p>
                </div>
              </div>
            </div>

            <div className="bg-[#FAFAF8] rounded-3xl p-10 mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Financial Projections Matter</h2>
              <p className="text-base text-gray-600 mb-8 leading-relaxed">
                Financial projections are essential for planning your business's future, securing funding, and making informed decisions. They help you understand your business's financial health and identify potential challenges before they become problems.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-[#D2691E]/10 rounded-lg flex items-center justify-center">
                      <i className="ri-bank-line text-xl text-[#D2691E]"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Attract Investors</h3>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Investors and lenders require detailed financial projections to assess the viability and potential return of your business.
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-[#D2691E]/10 rounded-lg flex items-center justify-center">
                      <i className="ri-compass-line text-xl text-[#D2691E]"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Guide Decisions</h3>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Use projections to make strategic decisions about hiring, expansion, pricing, and resource allocation.
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-[#D2691E]/10 rounded-lg flex items-center justify-center">
                      <i className="ri-alert-line text-xl text-[#D2691E]"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Identify Risks</h3>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Spot potential cash flow problems, funding gaps, and financial challenges before they threaten your business.
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-[#D2691E]/10 rounded-lg flex items-center justify-center">
                      <i className="ri-bar-chart-box-line text-xl text-[#D2691E]"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Measure Progress</h3>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Compare actual results against projections to track performance and adjust your strategy as needed.
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Key Financial Projections</h2>
              <p className="text-lg text-gray-600 mb-10 leading-relaxed">
                Master these four essential types of financial projections to build a comprehensive financial plan for your business.
              </p>
              
              <div className="space-y-8">
                {projectionTypes.map((type, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8">
                    <div className="flex items-start space-x-6 mb-6">
                      <div className="w-16 h-16 bg-[#D2691E] rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className={`${type.icon} text-3xl text-white`}></i>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{type.title}</h3>
                        <p className="text-base text-gray-600 leading-relaxed">{type.description}</p>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-6">
                      <h4 className="text-sm font-bold text-[#D2691E] mb-4 uppercase tracking-wide">Step-by-Step Process</h4>
                      <div className="space-y-3">
                        {type.steps.map((step, stepIndex) => (
                          <div key={stepIndex} className="flex items-start space-x-3">
                            <div className="w-7 h-7 bg-[#D2691E] rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-white text-xs font-bold">{stepIndex + 1}</span>
                            </div>
                            <span className="text-sm text-gray-700 leading-relaxed pt-1">{step}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">El Paso-Specific Considerations</h2>
              <p className="text-lg text-gray-600 mb-10 leading-relaxed">
                When creating financial projections for your El Paso business, consider these local factors that can impact your financial performance.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {elPasoConsiderations.map((consideration, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8">
                    <div className="w-16 h-16 bg-[#D2691E] rounded-xl flex items-center justify-center mb-6">
                      <i className={`${consideration.icon} text-3xl text-white`}></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">{consideration.title}</h3>
                    <ul className="space-y-3">
                      {consideration.points.map((point, pointIndex) => (
                        <li key={pointIndex} className="flex items-start space-x-3">
                          <i className="ri-checkbox-circle-fill text-[#D2691E] text-base flex-shrink-0 mt-0.5"></i>
                          <span className="text-sm text-gray-700 leading-relaxed">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#1B3A3A] rounded-3xl p-12 text-center mb-16">
              <h3 className="text-4xl font-bold text-white mb-4">Need Help with Your Projections?</h3>
              <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
                Our team can help you create accurate, investor-ready financial projections tailored to the El Paso market.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                <Link
                  to="/resources/market-research-tools"
                  className="inline-flex items-center space-x-3 bg-white text-[#1B3A3A] px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition-all shadow-lg cursor-pointer whitespace-nowrap"
                  aria-label="View Market Research Tools"
                >
                  <span>Market Research Tools</span>
                  <i className="ri-arrow-right-line text-xl"></i>
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-3 bg-[#D2691E] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#B8571A] transition-all shadow-lg cursor-pointer whitespace-nowrap"
                  aria-label="Contact us for expert assistance"
                >
                  <span>Get Expert Help</span>
                  <i className="ri-arrow-right-line text-xl"></i>
                </Link>
              </div>
            </div>
          </div>

          <RelatedResources resources={relatedResources} />
        </main>

        <Footer />
      </div>
    </>
  );
}